/* WeQuote Dev Board — Calendar core (PRD §6 Calendar; verify §57/§58/§67).
 * Pure ports: view config, Monday anchor, ISO week number, row membership
 * (dev rows = dated cards; design row = in-design with start+deadline;
 * coming-soon = pipeline cards on neither), interval laning for overlapping
 * bars (sub-day cards packed onto one day stack into sub-rows), working-day
 * counting for resize, and the three edit operations — resize (re-derives the
 * estimate), move-start (pins), reassign (pins on the new lane). */
import type { Feature, Blockout } from './types'
import { DEVCOLS, DL, calcEffort, inPipeline } from './constants'
import { parseISO, toISO, snapWorking, nextWorkingDay, isWorkingDay, setScheduleBlockouts } from './logic'
import { datedCard } from './schedule'
import type { MutResult } from './mutations'

export type CalView = 'days' | 'weeks' | 'months'
export interface CalCfg { weeks: number; dayW: number; barH: number }
export function calCfg(view: CalView): CalCfg {
  if (view === 'days') return { weeks: 1, dayW: 150, barH: 60 }
  if (view === 'months') return { weeks: 18, dayW: 22, barH: 24 }
  return { weeks: 8, dayW: 46, barH: 38 } // weeks
}
export function calMonday(d: Date): Date {
  const x = new Date(d.getTime())
  x.setHours(0, 0, 0, 0)
  const w = x.getDay()
  x.setDate(x.getDate() + (w === 0 ? -6 : 1 - w))
  return x
}
export function calWeekNo(d: Date): number {
  const t = new Date(d.getTime())
  t.setHours(0, 0, 0, 0)
  t.setDate(t.getDate() + 3 - ((t.getDay() + 6) % 7))
  const w1 = new Date(t.getFullYear(), 0, 4)
  return 1 + Math.round(((t.getTime() - w1.getTime()) / 86400000 - 3 + ((w1.getDay() + 6) % 7)) / 7)
}

export function calOnDevRow(f: Feature): boolean { return datedCard(f) && !f.multiStage } // staged parents render as per-stage bars (v2 §2)
export function calOnDesignRow(f: Feature): boolean {
  return f.status === 'design' && !!f.designStart && !!f.designDeadline
}
export function calComingSoon(f: Feature): boolean {
  // v2 §5: only the NOW band queues to schedule — Next/Later stay out of the way
  return f.status === 'now' && !calOnDevRow(f) && !calOnDesignRow(f)
}

/** Next working day after the dev's last scheduled est completion (or today). */
export function devNextAvailable(dev: string, features: Feature[], blockouts: Blockout[], now?: Date): string {
  setScheduleBlockouts(blockouts)
  let last = ''
  features.forEach((f) => { if (f.dev === dev && datedCard(f) && f.estCompletion && f.estCompletion > last) last = f.estCompletion })
  return last ? toISO(nextWorkingDay(parseISO(last), dev)) : toISO(snapWorking(now ? new Date(now.getTime()) : new Date(), dev))
}

/** Interval laning: overlapping bars stack into sub-rows (same-day sub-day
 *  cards share a day so they overlap; a bar fits an existing lane only when it
 *  starts strictly after that lane's current end). */
export interface Laned<T> { items: T[]; assign: number[]; n: number }
export function calLanes<T extends Record<string, any>>(list: T[], sK: string, eK: string): Laned<T> {
  const items = list.slice().sort((a, b) => (a[sK] < b[sK] ? -1 : a[sK] > b[sK] ? 1 : 0))
  const ends: string[] = []
  const assign: number[] = []
  items.forEach((it) => {
    let placed = false
    for (let l = 0; l < ends.length; l++) {
      if (it[sK] > ends[l]) { assign.push(l); ends[l] = it[eK]; placed = true; break }
    }
    if (!placed) { assign.push(ends.length); ends.push(it[eK]) }
  })
  return { items, assign, n: Math.max(1, ends.length) }
}

export function calDaysISO(iso: string, n: number): string {
  const d = parseISO(iso)
  d.setDate(d.getDate() + n)
  return toISO(d)
}
export function calWorkingDaysBetween(sISO: string, eISO: string, dev: string | null): number {
  if (!sISO || !eISO || eISO < sISO) return 1
  let n = 0
  const d = parseISO(sISO)
  while (toISO(d) <= eISO) {
    if (isWorkingDay(d, dev)) n++
    d.setDate(d.getDate() + 1)
  }
  return Math.max(1, n)
}

/* ── edit operations (pure) ── */
/** Dragging the right edge re-derives the ESTIMATE from working days (start→end). */
export function calResizeCard(fs: Feature[], id: number, schedStart: Record<number, string>, newEndISO: string, blockouts: Blockout[]): MutResult {
  setScheduleBlockouts(blockouts)
  const f = fs.find((x) => x.id === id)
  if (!f) return { features: fs }
  const s = schedStart[f.id] || f.start
  if (!s) return { features: fs }
  const end = newEndISO < s ? s : newEndISO
  const time = String(calWorkingDaysBetween(s, end, f.dev))
  const e = calcEffort(time)
  return {
    features: fs.map((x) => (x.id === id ? { ...x, time, eff: e || x.eff } : x)),
    changedIds: [id],
  }
}
/** Dragging a bar sets a new (snapped) start and PINS it — reality wins. */
export function calMoveStart(fs: Feature[], id: number, newStartISO: string, blockouts: Blockout[]): MutResult {
  setScheduleBlockouts(blockouts)
  const f = fs.find((x) => x.id === id)
  if (!f) return { features: fs }
  const start = toISO(snapWorking(parseISO(newStartISO), f.dev))
  return { features: fs.map((x) => (x.id === id ? { ...x, start, pinnedStart: true } : x)), changedIds: [id] }
}
/** Dropping on another developer's lane reassigns (and pins at the drop date). */
export function calReassign(fs: Feature[], id: number, dev: string, newStartISO: string | null, blockouts: Blockout[]): MutResult {
  setScheduleBlockouts(blockouts)
  const f = fs.find((x) => x.id === id)
  if (!f || !dev || !DL[dev]) return { features: fs }
  let title = ''
  const next = fs.map((x) => {
    if (x.id !== id) return x
    title = x.title
    const g = { ...x, dev }
    if (newStartISO) {
      g.start = toISO(snapWorking(parseISO(newStartISO), dev))
      g.pinnedStart = true
    }
    return g
  })
  return { features: next, toast: '“' + title + '” moved to ' + DL[dev] + '’s lane.', changedIds: [id] }
}

/* ── Lee, 3 Sep: CALENDAR-ONLY additions (the Roadmap is untouched) ── */

/** Cards with NO estimate still show on the calendar when they have a start
 *  or sit in a dev column — assumed at 1 working day so they're visible. */
export function calAssumedUnits(features: Feature[], dev: string, now?: Date): { f: Feature; s: string; e: string }[] {
  const out: { f: Feature; s: string; e: string }[] = []
  features.forEach((f) => {
    if (f.dev !== dev || f.multiStage) return
    if (!inPipeline(f)) return
    const est = parseFloat(f.time)
    if (f.time && !isNaN(est) && est > 0) return // has a real estimate → normal bar
    const inDevCol = DEVCOLS.indexOf(f.status) > -1
    if (!f.start && !inDevCol) return
    const d = snapWorking(f.start ? parseISO(f.start) : (now ? new Date(now.getTime()) : new Date()), dev)
    const iso = toISO(d)
    out.push({ f, s: iso, e: iso }) // one working day
  })
  return out
}
/** Completed (live) cards STAY on the calendar over the days they ran. */
export function calLiveUnits(features: Feature[], dev: string): { f: Feature; s: string; e: string }[] {
  const out: { f: Feature; s: string; e: string }[] = []
  features.forEach((f) => {
    if (f.dev !== dev || f.multiStage || f.status !== 'live') return
    if (!f.start || !f.completedOn) return
    out.push({ f, s: f.start <= f.completedOn ? f.start : f.completedOn, e: f.completedOn })
  })
  return out
}
/** Calendar bar tone: grey = not started · amber = in progress · green = done. */
export function calBarTone(f: Feature): 'p0' | 'active' | 'done' {
  if (f.status === 'live' || (f.progress || 0) >= 100) return 'done'
  return (f.progress || 0) > 0 ? 'active' : 'p0'
}
